import type { FolderTreeItem } from "@zapier/client";

export interface FolderTreeRowCounts {
  /** The query these counts describe, so stale counts are not announced. */
  searchQuery: string;
  /** Rendered rows whose own name matches the query. */
  matchCount: number;
  /** Rendered rows, including ancestors kept only for structure. */
  shownCount: number;
}

/**
 * Counts the rows the tree actually renders. Children of a collapsed folder,
 * and of a top-level shared folder while sharing is disabled, are not rendered
 * and so are not counted; the query is compared unmodified, the same way the
 * folder filter compares it.
 */
export function countFolderTreeRows(
  folders: FolderTreeItem[] | undefined,
  expandedFolders: Set<string>,
  searchQuery: string,
  disableSharedFolders = false,
): FolderTreeRowCounts {
  const needle = searchQuery.toLowerCase();
  let matchCount = 0;
  let shownCount = 0;

  const visit = (nodes: FolderTreeItem[], isTopLevel: boolean) => {
    for (const node of nodes) {
      shownCount++;
      if (needle && node.name?.toLowerCase().includes(needle)) {
        matchCount++;
      }
      // Only top-level rows receive `disabled`, and a disabled row renders no
      // children, so neither branch of the tree reaches them.
      const isDisabled = isTopLevel && disableSharedFolders && node.isShared;
      const children: FolderTreeItem[] | undefined = node.children;
      if (
        children?.length &&
        !isDisabled &&
        expandedFolders.has(String(node.id))
      ) {
        visit(children, false);
      }
    }
  };

  visit(folders ?? [], true);

  return { matchCount, shownCount, searchQuery };
}
